import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { documentaryService, DocumentaryQueryParams } from '@/services/documentaryService';
import { CreateDocumentaryRequest } from '@/types/model';

export const documentaryKeys = {
  all: ['documentaries'] as const,
  lists: () => [...documentaryKeys.all, 'list'] as const,
  list: (params: DocumentaryQueryParams) => [...documentaryKeys.lists(), params] as const,
  details: () => [...documentaryKeys.all, 'detail'] as const,
  detail: (id: string) => [...documentaryKeys.details(), id] as const,
};

export const useDocumentaries = (params: DocumentaryQueryParams = {}) => {
  const queryResult = useQuery({
    queryKey: documentaryKeys.list(params),
    queryFn: () => documentaryService.getDocumentaries(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  };
};

export const useDocumentary = (id: string) => {
  return useQuery({
    queryKey: documentaryKeys.detail(id),
    queryFn: () => documentaryService.getDocumentaryById(id),
    enabled: !!id,
  });
};

export const useCreateDocumentary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (documentaryData: CreateDocumentaryRequest) =>
      documentaryService.createDocumentary(documentaryData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentaryKeys.lists() });
    },
  });
};

export const useUpdateDocumentary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateDocumentaryRequest> }) =>
      documentaryService.updateDocumentary(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: documentaryKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: documentaryKeys.lists() });
    },
  });
};

export const useDeleteDocumentary = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => documentaryService.deleteDocumentary(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentaryKeys.lists() });
    },
  });
};

export const useToggleDocumentaryStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      documentaryService.toggleDocumentaryStatus(id, active),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: documentaryKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: documentaryKeys.lists() });
    },
  });
};